// Shows recent entries in one logbook / link to that logbook's full list, for
// display on an Item. An Item may have several logbooks; one of these cards is
// rendered per logbook (see ItemLogbooks).
//
//   item    - the Item the logbook belongs to
//   logbook - the Logbook to summarize (carries recent entries in
//             logbook.completedTasks.items)
//   onAll   - called when "See All" is clicked (navigates to the list)
//   onAdded(entry) - called when a logbook entry was added
//   onSaved(entry) - called when a logbook entry is edited and saved
//   onDelete(entry) - called when a logbook entry is deleted
//   onCounterSaved(counter) - optional; called when saving an entry moved the
//             item's counter (see itemCounterSync.mjs), so a host holding the
//             item can splice the new value in
import { useState } from 'react';
import { useRootCrumbNavigate } from '../item/RootCrumb';
import Button from 'react-bootstrap/Button';
import { useStatusMessage, LogbookEntrySummary, EditLogbookEntry, DataCard, LogbookHelp, PickLogbookAddType } from '../../components/index.js';
import * as Database from '../../utilities/Database.mjs';
import { syncItemCounterFromEntry } from '../../utilities/itemCounterSync.mjs';
import { urlForLogbookPickTask } from '../../fleet-shared/Urls.mjs';
import { supportsTaskCompletion } from '../../fleet-shared/LogbookTypes.mjs';
import { CardHeader } from '../../components/CardHeader.jsx';

// `placeIndex` comes from the PAGE (ItemLogbooks, AllTrips), not from a hook in
// here: a card is not where a fetch belongs, and a card that fetched would make
// every synchronous test of it resolve state outside act(). Absent, the rows
// show the full place names -- what they always showed.
const LogbookSummary = ( {item, logbook, placeIndex, onAll, onAdded, onSaved, onDelete, onCounterSaved } ) => {
    const [StatusMessage, {showText, showError, hideStatus}] = useStatusMessage();
    const navigate = useRootCrumbNavigate();

    const [showEditLogbookEntry, setShowEditLogbookEntry] = useState(false);
    const [editLogbookEntry, setEditLogbookEntry] = useState(null);

    const [showPickLogbook, setShowPickLogbook] = useState(false);

    function didAdd(savedTask) {
      // Update the UI
      onAdded(savedTask);
      setEditLogbookEntry(savedTask);
      setShowEditLogbookEntry(true);
    }

    async function handleAdd() {
      setShowPickLogbook(false);
      await Database.createCompletedTaskWithUI(logbook, showText, showError, hideStatus, didAdd);
    }

    function handleSelectTaskForLogbook() {
        // Navigate to logbook/picktask. Pass item + logbook via state so the
        // destination page (useSharedItemPageState) can skip a redundant fetch.
        navigate(urlForLogbookPickTask(item.id, logbook.id), { state: { item, logbook } });
    }


    // Passed to EditLogbookEntry. `entry` is already merged (id/itemId/logbookId
    // + split column/json fields). throws on errors.
    async function saveEditedLogbookEntry(entry) {
      // Update the database, can throw.
      let savedTask = await Database.updateCompletedTask(entry);

      // And UI. Runs per autosave flush -- must NOT close the modal.
      onSaved(savedTask);
      hideStatus();

      // The last entry of a Captain's Log moves the item's own counter (see
      // itemCounterSync.mjs for the guards). Fire-and-forget: a failed SYNC
      // must not fail the entry save that triggered it.
      syncItemCounterFromEntry({
        entry: savedTask,
        typeId: logbook?.logbookType,
        item,
        onCounterSaved,
      }).catch((err) => console.error('Item counter sync failed', err));
    }

    async function deleteLogbookEntry(entry) {
      // Go delete the item.
      await Database.deleteCompletedTask(entry.id);

      // Tell parent item was deleted so it can update its model.
      onDelete(entry);
    }

    // Cap the card at the most recent few entries. NOTE: the loader
    // (Database.listRecentCompletedTasksForLogbook, default limit 6) must fetch
    // at least numEntriesToShow + 1 so the ">5" check below can tell whether
    // more exist and surface "See All". If you raise this, raise that limit in
    // lockstep.
    const numEntriesToShow = 5;

    const Entries = () => {
        if (logbook) {
            const entries = logbook.completedTasks?.items ?? [];
            if (entries.length === 0) {
                return (
                    <LogbookHelp typeId={logbook?.logbookType} />
                );
            }

            // Sort by date descending (newest first) so the latest entry is on
            // top regardless of how the list was mutated by add/edit/delete,
            // then show only the most recent few.
            const sorted = [...entries].sort((a, b) => {
                if (a.date < b.date) return 1;
                if (a.date > b.date) return -1;
                return 0;
            });

            return (
                <>
                    {sorted.slice(0, numEntriesToShow).map( (entry) => (
                        <LogbookEntrySummary key={entry.id} entry={entry} item={item} logbook={logbook} placeIndex={placeIndex} showDelete={true} onDelete={deleteLogbookEntry}/>
                    ))}
                    {sorted.length > numEntriesToShow && (
                        <div className="text-center mt-2">
                            <Button variant="primary" size="sm" onClick={onAll}>See All</Button>
                        </div>
                    )}
                </>
            );
        }
        return null;
    }


    return (
        <div>
            <DataCard
              header=<CardHeader text={logbook ? logbook.name : 'Logbook'} addTapped={() => setShowPickLogbook(true)} seeAllTapped={onAll}/>
              body={
                <div className="row justify-content-center">
                    <div className="text-center center mb-2">
                        {StatusMessage()}
                        <div className="logbook-entries">
                            <Entries />
                        </div>
                    </div>
                </div>
              }
            />

            <EditLogbookEntry
              show={showEditLogbookEntry}
              onHide={() => setShowEditLogbookEntry(false)}
              task={editLogbookEntry}
              item={item}
              logbook={logbook}
              save={saveEditedLogbookEntry}
              deleteEntry={() => deleteLogbookEntry(editLogbookEntry)}
            />

            <PickLogbookAddType
              show={showPickLogbook}
              onHide={() => setShowPickLogbook(false)}
              showTaskOption={supportsTaskCompletion(logbook?.logbookType)}
              onSelectTask={handleSelectTaskForLogbook}
              onAddAdHoc={handleAdd}
            />

        </div>
    );
}

export default LogbookSummary;
